/**
 * useGameLoop hook
 *
 * Drives the main game loop using requestAnimationFrame.
 * Manages:
 *  - Note position updates (notes fall top to bottom)
 *  - Automatic MISS assignment for notes that pass the hit line
 *  - Hit particle lifecycle
 *  - Keyboard input handling
 *  - Count-in metronome
 *  - Game phase transitions
 */

import {
  useRef,
  useState,
  useCallback,
  useEffect,
  MutableRefObject,
} from 'react';
import {
  GameState,
  GameNote,
  HitParticle,
  GamePhase,
  KEY_TO_LANE,
} from '../types';
import { calculateScore, comboMultiplier, MAX_HIT_WINDOW_MS } from '../utils/timing';
import { findNearestNoteIndex } from '../utils/midiParser';
import { v4 as uuidv4 } from 'uuid';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Canvas width in pixels */
export const CANVAS_WIDTH = 900;

/** Canvas height in pixels */
export const CANVAS_HEIGHT = 600;

/** Pixels per millisecond the notes fall downward */
export const SCROLL_SPEED_PX_PER_MS = 0.45;

/** Y position of the hit line (near bottom of canvas) */
export const HIT_LINE_Y = 520;

/** Y position where notes spawn (above the canvas) */
export const SPAWN_Y = -50;

/**
 * How far ahead (ms) to spawn notes so they arrive at the hit line on time.
 * travelTime = (HIT_LINE_Y - SPAWN_Y) / SCROLL_SPEED_PX_PER_MS
 */
export const LEAD_TIME_MS = (HIT_LINE_Y - SPAWN_Y) / SCROLL_SPEED_PX_PER_MS;

export interface UseGameLoopReturn {
  gameState: GameState;
  phase: GamePhase;
  startGame: (notes: GameNote[], bpm: number) => void;
  stopGame: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  onAudioTimeUpdate: (currentMs: number) => void;
  setSpeedMultiplier: (m: number) => void;
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  countdownBeat: number;
}

const INITIAL_STATE: GameState = {
  phase: 'idle',
  score: 0,
  combo: 0,
  maxCombo: 0,
  perfectCount: 0,
  goodCount: 0,
  missCount: 0,
  currentTime: 0,
  notes: [],
  particles: [],
};

export function useGameLoop(): UseGameLoopReturn {
  const [gameState, setGameState] = useState<GameState>({ ...INITIAL_STATE });
  const [phase, setPhase] = useState<GamePhase>('idle');
  const [countdownBeat, setCountdownBeat] = useState(0);

  // Mutable ref mirrors for use inside RAF callbacks
  const stateRef = useRef<GameState>({ ...INITIAL_STATE });
  const bpmRef = useRef(120);
  const lastTimestampRef = useRef<number>(0);
  const rafIdRef = useRef<number>(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioTimeRef = useRef<number>(0);
  const speedMultiplierRef = useRef<number>(1);

  // -------------------------------------------------------------------------
  // State sync helper
  // -------------------------------------------------------------------------

  const syncState = useCallback((partial: Partial<GameState>) => {
    stateRef.current = { ...stateRef.current, ...partial };
    setGameState({ ...stateRef.current });
  }, []);

  // -------------------------------------------------------------------------
  // Keyboard input
  // -------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (stateRef.current.phase !== 'playing') return;
      if (!(key in KEY_TO_LANE)) return;

      e.preventDefault();

      const lane = KEY_TO_LANE[key];
      const currentMs = audioTimeRef.current;

      const idx = findNearestNoteIndex(
        stateRef.current.notes,
        lane,
        currentMs,
        MAX_HIT_WINDOW_MS
      );

      if (idx === -1) return;

      const note = stateRef.current.notes[idx];
      const result = calculateScore(note.time, currentMs, bpmRef.current);

      const updatedNotes = [...stateRef.current.notes];
      updatedNotes[idx] = {
        ...note,
        judged: true,
        rating: result.rating,
      };

      const multiplier =
        result.rating !== 'MISS'
          ? comboMultiplier(stateRef.current.combo)
          : 1;
      const newScore =
        stateRef.current.score + result.points * multiplier;
      const newCombo =
        result.rating !== 'MISS' ? stateRef.current.combo + 1 : 0;
      const newMaxCombo = Math.max(
        stateRef.current.maxCombo,
        newCombo
      );

      const particle: HitParticle = {
        id: uuidv4(),
        x: lane * (CANVAS_WIDTH / 6) + (CANVAS_WIDTH / 6) / 2,
        y: HIT_LINE_Y,
        lane,
        rating: result.rating,
        opacity: 1,
        age: 0,
      };

      syncState({
        score: newScore,
        combo: newCombo,
        maxCombo: newMaxCombo,
        perfectCount:
          stateRef.current.perfectCount +
          (result.rating === 'PERFECT' ? 1 : 0),
        goodCount:
          stateRef.current.goodCount +
          (result.rating === 'GOOD' ? 1 : 0),
        missCount:
          stateRef.current.missCount +
          (result.rating === 'MISS' ? 1 : 0),
        notes: updatedNotes,
        particles: [...stateRef.current.particles, particle],
      });
    },
    [syncState]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // -------------------------------------------------------------------------
  // Game loop tick
  // -------------------------------------------------------------------------

  const tick = useCallback(
    (timestamp: number) => {
      if (stateRef.current.phase !== 'playing') return;

      lastTimestampRef.current = timestamp;

      const currentMs = audioTimeRef.current;

      // Update note Y positions (notes fall from top toward hit line)
      const speed = SCROLL_SPEED_PX_PER_MS * speedMultiplierRef.current;
      const updatedNotes = stateRef.current.notes.map((note) => {
        if (note.judged) return note;
        const y = HIT_LINE_Y - (note.time - currentMs) * speed;
        return { ...note, y };
      });

      // Auto-miss notes that have passed the hit line by more than 200 ms
      let missesAdded = 0;
      const notesAfterMiss = updatedNotes.map((note) => {
        if (!note.judged && note.time < currentMs - MAX_HIT_WINDOW_MS / 2) {
          missesAdded++;
          return { ...note, judged: true, rating: 'MISS' as const };
        }
        return note;
      });

      // Age out particles
      const updatedParticles = stateRef.current.particles
        .map((p) => ({ ...p, age: p.age + 1, opacity: p.opacity - 0.05 }))
        .filter((p) => p.opacity > 0);

      const newMissCount =
        stateRef.current.missCount + missesAdded;
      const newCombo =
        missesAdded > 0 ? 0 : stateRef.current.combo;

      // Check if all notes have been judged
      const allJudged = notesAfterMiss.every((n) => n.judged);

      if (allJudged) {
        syncState({
          phase: 'finished',
          notes: notesAfterMiss,
          particles: updatedParticles,
          missCount: newMissCount,
          combo: newCombo,
          currentTime: currentMs,
        });
        setPhase('finished');
        return;
      }

      syncState({
        notes: notesAfterMiss,
        particles: updatedParticles,
        missCount: newMissCount,
        combo: newCombo,
        currentTime: currentMs,
      });

      rafIdRef.current = requestAnimationFrame(tick);
    },
    [syncState]
  );

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  const startGame = useCallback(
    (notes: GameNote[], bpm: number) => {
      bpmRef.current = bpm;
      audioTimeRef.current = 0;
      lastTimestampRef.current = 0;

      // Reset positions (initial Y: notes above canvas, falling toward hit line)
      const speed = SCROLL_SPEED_PX_PER_MS * speedMultiplierRef.current;
      const initialNotes: GameNote[] = notes.map((note) => ({
        ...note,
        judged: false,
        rating: undefined,
        y: HIT_LINE_Y - note.time * speed,
      }));

      const fresh: GameState = {
        ...INITIAL_STATE,
        phase: 'countdown',
        notes: initialNotes,
      };
      stateRef.current = fresh;
      setGameState(fresh);
      setPhase('countdown');

      // 4-beat count-in
      const beatMs = 60_000 / bpm;
      let beat = 0;
      const countIn = setInterval(() => {
        beat++;
        setCountdownBeat(beat);
        if (beat >= 4) {
          clearInterval(countIn);
          stateRef.current = { ...stateRef.current, phase: 'playing' };
          setGameState({ ...stateRef.current });
          setPhase('playing');
          lastTimestampRef.current = 0;
          rafIdRef.current = requestAnimationFrame(tick);
        }
      }, beatMs);
    },
    [tick]
  );

  const stopGame = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);
    stateRef.current = { ...INITIAL_STATE };
    setGameState({ ...INITIAL_STATE });
    setPhase('idle');
    setCountdownBeat(0);
    audioTimeRef.current = 0;
  }, []);

  const pauseGame = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);
    stateRef.current = { ...stateRef.current, phase: 'paused' };
    setGameState({ ...stateRef.current });
    setPhase('paused');
  }, []);

  const resumeGame = useCallback(() => {
    stateRef.current = { ...stateRef.current, phase: 'playing' };
    setGameState({ ...stateRef.current });
    setPhase('playing');
    lastTimestampRef.current = 0;
    rafIdRef.current = requestAnimationFrame(tick);
  }, [tick]);

  /** Called by parent component to keep audio time in sync */
  const onAudioTimeUpdate = useCallback((currentMs: number) => {
    audioTimeRef.current = currentMs;
  }, []);

  /** Update the visual scroll speed multiplier (does not affect audio rate) */
  const setSpeedMultiplier = useCallback((m: number) => {
    speedMultiplierRef.current = m;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  return {
    gameState,
    phase,
    startGame,
    stopGame,
    pauseGame,
    resumeGame,
    onAudioTimeUpdate,
    setSpeedMultiplier,
    canvasRef,
    countdownBeat,
  };
}
